import React from "react";
import { CheckCircleFill } from "../icons";
import { useTranslation, Trans } from "react-i18next";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import { useUsesDesign } from "../context/GuestCheckoutSettingsContext";

interface BookingActionSummaryCardProps {
  /** Whether the guest added any pre-order items. */
  readonly hasPreorder: boolean;
  /** Whether a payment was taken (money charged). */
  readonly didPay: boolean;
  /** Whether the card was authenticated only (no money taken). */
  readonly didAuthenticate: boolean;
  /** Amount paid or authenticated. */
  readonly amount: number;
  readonly currency: string;
  readonly countryCode: string | null;
}

/**
 * BookingActionSummaryCard — a status box shown on the /book confirmation that
 * summarises what the guest did: added a pre-order, paid, authenticated a card,
 * or a combination of these. Reuses the payment-success-card styling.
 *
 * Renders nothing when the guest neither pre-ordered, paid nor authenticated.
 */
const BookingActionSummaryCard: React.FC<BookingActionSummaryCardProps> = ({
  hasPreorder,
  didPay,
  didAuthenticate,
  amount,
  currency,
  countryCode,
}) => {
  const { t } = useTranslation();
  const usesDesign = useUsesDesign();

  if (!hasPreorder && !didPay && !didAuthenticate) {
    return null;
  }

  const formattedAmount = formatCurrency(amount, currency, countryCode ?? "GB");

  const getHeading = (): string => {
    if (didAuthenticate) return t("booking_summary_auth_title", "Card Authenticated");
    if (didPay) return t("payment_successful_title", "Payment Successful");
    return t("order_received", "Order Received");
  };

  const getMessage = (): React.ReactNode => {
    if (didAuthenticate) {
      const key = hasPreorder ? "booking_summary_preorder_auth" : "payment_successful_message_auth";
      return <Trans i18nKey={key} values={{ amount: formattedAmount }} components={{ strong: <strong /> }} />;
    }
    if (didPay) {
      const key = hasPreorder ? "booking_summary_preorder_payment" : "payment_successful_message_payment";
      return <Trans i18nKey={key} values={{ amount: formattedAmount }} components={{ strong: <strong /> }} />;
    }
    return t("booking_summary_preorder_only", "Your pre-order has been added to your booking.");
  };

  return (
    <div className="gcw:rounded-lg gcw:border gcw:border-gray-200 gcw:bg-white gcw:shadow-sm payment-success-card gcw:mb-4">
      <div className="gcw:p-bs-3">
        <div className="gcw:flex gcw:items-center gcw:mb-2">
          {!usesDesign && <CheckCircleFill size={24} className="gcw:text-info gcw:me-2" aria-hidden="true" />}
          <h2 className="gcw:text-subtitle gcw:mb-0">{getHeading()}</h2>
        </div>
        <p className="gcw:mb-0 gcw:text-ref">{getMessage()}</p>
      </div>
    </div>
  );
};

export default BookingActionSummaryCard;
